/**
 * Zod schemas + inferred types for the logins feature.
 *
 * Mirrors backend/features/logins/schemas.py field-for-field.
 */

import { z } from 'zod';

export const LoginStatusSchema = z.object({
  name: z.string(),
  display_name: z.string(),
  type: z.string(),
  acquisition_mode: z.string().nullable(),
  status: z.enum(['connected', 'attention', 'not_connected']),
  reason: z.enum(['needs_link', 'needs_reauth', 'provider_error']).nullable(),
  identity: z.string().nullable(),
  features: z.array(z.string()),
  git_servers: z.array(z.string()),
  secret_label: z.string().nullable(),
  help_text: z.string().nullable(),
});
export type LoginStatus = z.infer<typeof LoginStatusSchema>;

export const LoginStatusListSchema = z.array(LoginStatusSchema);

export const LoginStartSchema = z.object({
  url: z.string(),
  state: z.string(),
  code_verifier: z.string(),
});
export type LoginStart = z.infer<typeof LoginStartSchema>;

export const ProviderSchema = z.object({
  id: z.string(),
  name: z.string(),
  display_name: z.string(),
  enabled: z.boolean(),
  type: z.string(),
  acquisition_mode: z.string().nullable(),
  broker_mechanism: z.string().nullable(),
  broker_alias: z.string().nullable(),
  issuer_url: z.string().nullable(),
  authorization_endpoint: z.string().nullable(),
  token_endpoint: z.string().nullable(),
  client_id: z.string().nullable(),
  has_client_secret: z.boolean(),
  scopes: z.string().nullable(),
  extra_auth_params: z.record(z.string(), z.unknown()).nullable(),
  secret_label: z.string().nullable(),
  help_text: z.string().nullable(),
  github_app_kind: z.enum(['app', 'oauth']).nullable(),
  git_servers: z.array(z.string()),
});
export type Provider = z.infer<typeof ProviderSchema>;

export const ProviderListSchema = z.array(ProviderSchema);

export const BindingSchema = z.object({
  id: z.string(),
  provider_id: z.string(),
  feature_id: z.string(),
  feature_name: z.string(),
});
export type Binding = z.infer<typeof BindingSchema>;

export const BindingListSchema = z.array(BindingSchema);

export const TestConnectionSchema = z.object({
  ok: z.boolean(),
  detail: z.string(),
});
export type TestConnection = z.infer<typeof TestConnectionSchema>;

/**
 * Request body for creating/updating a login provider.
 *
 * Mirrors backend `ProviderRequest`; `client_secret` is write-only and
 * never present in `ProviderResponse`.
 */
export interface ProviderRequestBody {
  name: string;
  display_name: string;
  enabled: boolean;
  type: 'oidc' | 'static';
  acquisition_mode: 'brokered' | 'direct' | null;
  broker_mechanism: 'keycloak-broker' | 'token-exchange' | null;
  broker_alias: string | null;
  issuer_url: string | null;
  authorization_endpoint: string | null;
  token_endpoint: string | null;
  client_id: string | null;
  client_secret?: string | null;
  scopes: string | null;
  extra_auth_params: Record<string, unknown> | null;
  secret_label: string | null;
  help_text: string | null;
  github_app_kind: 'app' | 'oauth' | null;
}
